import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { AIAssistCard } from "@/components/AIAssistCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import VoiceInput from "@/components/VoiceInput";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Printer, ArrowRightCircle, Percent, Receipt, FileText, Clock, Search, CalendarIcon, Eye, UserCog } from "lucide-react";
import { usePerms } from "@/context/PermContext";
import { useAuth } from "@/context/AuthContext";
import MedicineRow from "@/components/MedicineRow";
import InvestigationDetailDialog from "@/components/InvestigationDetailDialog";
import TemplatePicker from "@/components/TemplatePicker";
import PrintBundle from "@/components/PrintBundle";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PROC_OPTIONS = ["PLASTER", "MINOR SURGERY", "MAJOR SURGERY", "DRESSING", "SUTURE REMOVAL", "CATHETERIZATION", "NEBULIZATION", "OTHER"];
const INJECTION_ROUTES = ["IV", "IM", "SC", "SubQ", "ID"];
const FLUID_ROUTES = ["IV", "Oral"];
const FLUID_TYPES = ["NS (Normal Saline)", "DNS (D5-NS)", "RL (Ringer Lactate)", "D5W (5% Dextrose)", "D10W (10% Dextrose)", "Isolyte-P", "Isolyte-M", "Other"];
const DETAIL_CATEGORIES = ["X-RAY", "CT", "MRI", "USG", "ECHO", "LAB"];

export default function OPDConsultation() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = usePerms();
  const { user } = useAuth();
  const [opd, setOpd] = useState(null);
  const [beds, setBeds] = useState([]);
  const [form, setForm] = useState({});
  const [bill, setBill] = useState(null);
  const [discOpen, setDiscOpen] = useState(false);
  const [disc, setDisc] = useState({ amount: 0, reason: "" });
  const [invMaster, setInvMaster] = useState([]);
  const [invQ, setInvQ] = useState("");
  const [invCat, setInvCat] = useState("ALL");
  const [invDetailFor, setInvDetailFor] = useState(null); // master row when dialog open
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const savedSnapshotRef = useRef("");  // JSON of form at last save (baseline)
  const savingRef = useRef(false);      // prevents overlapping saves

  // Change-doctor dialog state
  const [docDlgOpen, setDocDlgOpen] = useState(false);
  const [docList, setDocList] = useState([]);
  const [docChange, setDocChange] = useState({ doctor_id: "", reason: "", adjust_fee: false });

  const loadAll = () => {
    api.get(`/opd/${id}`).then((r) => {
      // One-shot migration: lift legacy "INJECTION"/"FLUIDS" entries from the
      // flat procedures[] array into procedure_entries so they can be edited
      // in the structured UI without losing data.
      const d = { ...r.data };
      const entries = Array.isArray(d.procedure_entries) ? [...d.procedure_entries] : [];
      const haveInjCount = entries.filter(e => e.name === "INJECTION").length;
      const haveFldCount = entries.filter(e => e.name === "FLUIDS").length;
      const procs = Array.isArray(d.procedures) ? d.procedures : [];
      const legacyInj = procs.filter(p => p === "INJECTION").length;
      const legacyFld = procs.filter(p => p === "FLUIDS").length;
      for (let i = haveInjCount; i < legacyInj; i++) {
        entries.push({ id: `inj-mig-${i}-${Date.now()}`, name: "INJECTION", route: "IV", drug: "", note: "" });
      }
      for (let i = haveFldCount; i < legacyFld; i++) {
        entries.push({ id: `fld-mig-${i}-${Date.now()}`, name: "FLUIDS", route: "IV", drug: "NS (Normal Saline)", volume: "500 ml", note: "" });
      }
      d.procedure_entries = entries;
      setOpd(r.data);
      setForm(d);
      savedSnapshotRef.current = JSON.stringify(d || {});
    });
    api.get(`/opd/${id}/billing`).then((r) => setBill(r.data));
  };

  useEffect(() => {
    loadAll();
    api.get("/beds", { params: { status: "available" } }).then((r) => setBeds(r.data));
    api.get("/investigation-master", { params: { active: "true" } }).then((r) => setInvMaster(r.data));
    api.get("/doctors").then((r) => setDocList(r.data || [])).catch(() => setDocList([]));
  }, [id]);

  const openDoctorChange = () => {
    setDocChange({ doctor_id: "", reason: "", adjust_fee: false });
    setDocDlgOpen(true);
  };
  const submitDoctorChange = async () => {
    if (!docChange.doctor_id) return toast.error("Select a doctor");
    if (!docChange.reason.trim()) return toast.error("Reason is required");
    try {
      const { data } = await api.put(`/opd/${id}/change-doctor`, docChange);
      toast.success(`Doctor changed to ${data.doctor_name}`);
      setDocDlgOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change doctor");
    }
  };

  // ─── Draft auto-save (every 30 s while dirty) ────────────────────────────
  const autoSave = async () => {
    if (savingRef.current) return;
    const snap = JSON.stringify(form || {});
    if (!snap || snap === savedSnapshotRef.current) return;   // nothing new to persist
    savingRef.current = true;
    setAutoSaving(true);
    try {
      await api.put(`/opd/${id}`, form);
      savedSnapshotRef.current = snap;
      setLastSavedAt(new Date());
    } catch (e) {
      // Auto-save is best-effort; the operator can still Save manually.
      // Log for debugging without alarming the user.
      console.warn("[OPD auto-save skipped]", e?.response?.status || e?.message);
    }
    finally {
      savingRef.current = false;
      setAutoSaving(false);
    }
  };

  useEffect(() => {
    if (!opd) return;
    const t = setInterval(autoSave, 30000);
    // Best-effort save when the tab is being hidden / closed
    const onVis = () => { if (document.visibilityState === "hidden") autoSave(); };
    window.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); window.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opd, id, form]);

  if (!opd) return <div>Loading...</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setV = (k, v) => setForm((f) => ({ ...f, vitals: { ...(f.vitals || {}), [k]: v } }));

  const toggleArr = (key, val) => {
    const arr = form[key] || [];
    set(key, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const addMed = () => set("medicines", [...(form.medicines || []), { _uid: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: "", dose: "1-0-1", food: "AFTER FOOD", duration: "5 DAYS", _custom: false }]);
  const updMed = (i, newVal) => set("medicines", (form.medicines || []).map((m, idx) => idx === i ? { ...m, ...newVal } : m));
  const rmMed = (i) => set("medicines", (form.medicines || []).filter((_, idx) => idx !== i));
  const toggleCustom = (i) => set("medicines", (form.medicines || []).map((m, idx) => idx === i ? { ...m, _custom: !m._custom } : m));

  const save = async () => {
    try {
      await api.put(`/opd/${id}`, form);
      savedSnapshotRef.current = JSON.stringify(form || {});
      setLastSavedAt(new Date());
      toast.success("Saved");
      loadAll();
    } catch { toast.error("Save failed"); }
  };

  const openPreview = async () => {
    try {
      await api.put(`/opd/${id}`, form);
      setPreviewKey((k) => k + 1); // force iframe reload with latest saved state
      setPreviewOpen(true);
    } catch { toast.error("Could not open preview — save failed"); }
  };

  const convert = async () => {
    const bed = beds[0];
    if (!bed) return toast.error("No available bed");
    await save();
    // Admins can override the auto-generated IPD No at admission time.
    let customIpdNo = "";
    if (["admin", "super_admin"].includes(user?.role)) {
      const typed = window.prompt(
        "Custom IPD No (optional — leave blank to auto-generate).\n\nExample: IPD/2026/001234",
        ""
      );
      if (typed === null) return;              // user cancelled the admit
      customIpdNo = (typed || "").trim();
    }
    try {
      const payload = { bed_id: bed.id };
      if (customIpdNo) payload.ipd_no = customIpdNo;
      const { data } = await api.post(`/opd/${id}/convert-to-ipd`, payload);
      toast.success(`Admitted as ${data.ipd_no}`);
      nav(`/ipd/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Admission failed");
    }
  };

  const requestDiscount = async () => {
    if (!disc.amount || disc.amount <= 0) return toast.error("Amount required");
    try {
      await api.post("/discount-requests", { opd_id: id, patient_id: opd.patient_id, amount: parseFloat(disc.amount), reason: disc.reason });
      toast.success("Discount request submitted for admin approval");
      setDiscOpen(false); setDisc({ amount: 0, reason: "" }); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const v = form.vitals || {};

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 p-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-xl font-semibold">{opd.patient_name}</h1>
              <Badge className="rounded-sm">{opd.opd_no}</Badge>
              {opd.pmjay && <Badge className="bg-teal-100 text-teal-800 rounded-sm">PMJAY</Badge>}
              {bill?.pending_discount && <Badge className="bg-amber-100 text-amber-800 rounded-sm"><Clock className="w-3 h-3 mr-1 inline"/> Discount Pending</Badge>}
              {bill?.approved_discount && <Badge className="bg-green-100 text-green-800 rounded-sm">Discount Approved ₹{bill.approved_discount.amount}</Badge>}
            </div>
            <div className="mt-2 text-xs text-slate-600 flex gap-4 flex-wrap items-center">
              <span>UHID: <span className="font-mono">{opd.uhid}</span></span>
              <span>Doctor: <b>{opd.doctor_name || <span className="text-red-600">Not assigned</span>}</b> · {opd.department}</span>
              <button
                type="button"
                onClick={openDoctorChange}
                data-testid="opd-change-doctor-btn"
                className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 underline underline-offset-2"
              >
                <UserCog className="w-3 h-3"/> Change doctor
              </button>
              {(opd.doctor_change_history || []).length > 0 && (
                <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5" title={opd.doctor_change_history.map(h=>`${h.at?.slice(0,16)?.replace('T',' ')} · ${h.from_doctor_name || '—'} → ${h.to_doctor_name} · ${h.reason}`).join('\n')}>
                  Reassigned {opd.doctor_change_history.length}×
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <span data-testid="opd-autosave-status" className="text-[11px] text-slate-500 mr-1 min-w-[80px] text-right">
              {autoSaving
                ? <span className="text-blue-700">Auto-saving…</span>
                : lastSavedAt
                  ? <>Saved <span className="font-mono">{lastSavedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span></>
                  : <span className="text-slate-400">Not saved yet</span>}
            </span>
            <Button data-testid="opd-save" onClick={save} className="bg-blue-700 hover:bg-blue-800 rounded-sm">Save Consultation</Button>
            <Button data-testid="opd-preview-rx" variant="outline" onClick={openPreview} className="rounded-sm border-blue-400 text-blue-800"><Eye className="w-4 h-4 mr-1"/> Preview Rx</Button>
            <Button data-testid="opd-print-rx" variant="outline" onClick={() => window.open(`/prescription/${id}/print`, "_blank")} className="rounded-sm"><Printer className="w-4 h-4 mr-1"/> Rx</Button>
            <Button data-testid="opd-print-bill-a4" variant="outline" onClick={() => window.open(`/bill/opd/${id}/print?layout=a4`, "_blank")} className="rounded-sm"><FileText className="w-4 h-4 mr-1"/> PRINT FULL BILL (A4)</Button>
            <div className="flex gap-1">
              <Button data-testid="opd-print-thermal-58" variant="outline" size="sm" onClick={() => window.open(`/bill/opd/${id}/print?layout=thermal58`, "_blank")} className="rounded-sm h-9"><Receipt className="w-4 h-4 mr-1"/> 58mm</Button>
              <Button data-testid="opd-print-thermal-80" variant="outline" size="sm" onClick={() => window.open(`/bill/opd/${id}/print?layout=thermal80`, "_blank")} className="rounded-sm h-9">80mm</Button>
            </div>
            {form.admission_advice && <Button data-testid="opd-convert-ipd" variant="outline" onClick={convert} className="rounded-sm border-teal-600 text-teal-800"><ArrowRightCircle className="w-4 h-4 mr-1"/> Convert to IPD</Button>}
            <Button data-testid="opd-push-abdm" variant="outline" onClick={async () => {
              try {
                const { data } = await api.post("/abdm/health-record/push", { opd_id: id });
                if (data.ok) toast.success(`Pushed to ABDM · ${data.record.bundle_size_entries} FHIR entries`);
                else toast.error(data.response?.message || `HTTP ${data.http_status || 0}`);
              } catch (e) { toast.error(e.response?.data?.detail || "Push failed"); }
            }} className="rounded-sm border-purple-600 text-purple-800"><FileText className="w-4 h-4 mr-1"/> Push to ABDM</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 p-4 space-y-3">
            <div className="label-tiny">Clinical</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Chief Complaints</Label>
                <VoiceInput testid="opd-cc" value={form.chief_complaint || ""} onChange={(v) => set("chief_complaint", v)} rows={2} placeholder="Fever × 3 days, cough…"/>
                <TemplatePicker type="complaint" value={form.chief_complaint} onChange={(v) => set("chief_complaint", v)} testid="tpl-cc"/>
              </div>
              <div>
                <Label>Clinical Findings</Label>
                <VoiceInput testid="opd-cf" value={form.clinical_findings || ""} onChange={(v) => set("clinical_findings", v)} rows={2} placeholder="BP 130/80, chest clear…"/>
                <TemplatePicker type="examination" value={form.clinical_findings} onChange={(v) => set("clinical_findings", v)} testid="tpl-cf"/>
              </div>
              <div>
                <Label>Diagnosis</Label>
                <VoiceInput testid="opd-dx" value={form.diagnosis || ""} onChange={(v) => set("diagnosis", v)} rows={2} placeholder="Acute pharyngitis"/>
                <TemplatePicker type="diagnosis" value={form.diagnosis} onChange={(v) => set("diagnosis", v)} testid="tpl-dx"/>
              </div>
              <div>
                <Label>Treatment</Label>
                <VoiceInput testid="opd-tx" value={form.treatment || ""} onChange={(v) => set("treatment", v)} rows={2} placeholder="Rest, hydration, Rx below"/>
                <TemplatePicker type="treatment" value={form.treatment} onChange={(v) => set("treatment", v)} testid="tpl-tx"/>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-4 space-y-3">
            <div className="label-tiny">Investigations & Procedures</div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Investigations <span className="text-[10px] text-slate-400">(advice only · reception bills)</span></Label>
                <div className="text-[11px] text-slate-500">Selected: <b>{(form.investigations || []).length}</b></div>
              </div>
              <div className="flex gap-2 mb-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
                  <Input value={invQ} onChange={(e) => setInvQ(e.target.value)} placeholder="Search investigations" className="pl-7 h-9" data-testid="opd-inv-search"/>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {["ALL", "X-RAY", "USG", "CT", "MRI", "ECG", "ECHO", "LAB"].map((c) => (
                    <button key={c} data-testid={`opd-inv-cat-${c}`} onClick={() => setInvCat(c)}
                      className={`h-9 px-2 text-[11px] border rounded-sm ${invCat === c ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 text-slate-600"}`}>{c}</button>
                  ))}
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto border rounded-sm p-2 bg-slate-50/60">
                <div className="flex flex-wrap gap-1.5">
                  {invMaster
                    .filter((m) => invCat === "ALL" || m.category === invCat)
                    .filter((m) => !invQ || (m.name + " " + (m.code || "") + " " + (m.description || "")).toLowerCase().includes(invQ.toLowerCase()))
                    .slice(0, 200)
                    .map((m) => {
                      const active = (form.investigations || []).includes(m.name);
                      const notes = (form.investigation_notes || {})[m.name];
                      const needsDetail = DETAIL_CATEGORIES.includes(m.category);
                      const summary = notes ? [
                        (notes.body_parts && notes.body_parts.length) ? notes.body_parts.join("+") : notes.body_part,
                        (notes.views && notes.views.length) ? notes.views.join("/") : null,
                        notes.contrast && notes.contrast !== "No" ? notes.contrast : null,
                        notes.urgency && notes.urgency !== "Routine" ? notes.urgency : null,
                      ].filter(Boolean).join(" · ") : "";
                      return (
                        <button key={m.id} type="button" onClick={() => {
                          if (needsDetail) {
                            // Open modal — dialog handles adding/updating detail + toggling selection
                            setInvDetailFor(m);
                            return;
                          }
                          // Simple category (ECG etc.) — plain toggle
                          if (active) {
                            const copy = { ...(form.investigation_notes || {}) };
                            delete copy[m.name];
                            set("investigation_notes", copy);
                          }
                          toggleArr("investigations", m.name);
                        }}
                          data-testid={`inv-opt-${m.code || m.name}`}
                          title={summary || m.description}
                          className={`px-2.5 py-1 text-[11px] border rounded-sm text-left transition ${active ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 bg-white hover:border-blue-400"}`}>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] font-mono text-slate-400">{m.category}</span>
                            <span>{m.name}</span>
                            {active && summary && <span className="text-[9px] text-teal-700 truncate max-w-[160px]">· {summary}</span>}
                            {active && needsDetail && (
                              <span title="Remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const copy = { ...(form.investigation_notes || {}) };
                                  delete copy[m.name];
                                  set("investigation_notes", copy);
                                  toggleArr("investigations", m.name);
                                }}
                                data-testid={`inv-remove-${m.code || m.name}`}
                                className="ml-1 text-red-500 hover:text-red-700 text-[11px] leading-none">×</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                </div>
                {invMaster.length === 0 && <div className="text-[11px] text-slate-500 p-2">Investigation master is empty. Ask an admin to seed it via /investigations-master.</div>}
              </div>
            </div>
            <div>
              <Label>Procedures <span className="text-[10px] text-slate-400">(advice only · reception bills · click to add notes · multi-entry for Injection / Fluids)</span></Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PROC_OPTIONS.map((i) => (
                  <button key={i} type="button" data-testid={`proc-${i}`} onClick={() => {
                    const sel = form.procedures?.includes(i);
                    if (sel) {
                      // De-selecting → also drop its note
                      const copy = { ...(form.procedure_notes || {}) };
                      delete copy[i];
                      set("procedure_notes", copy);
                    }
                    toggleArr("procedures", i);
                  }}
                    className={`px-3 py-1 text-xs border rounded-sm ${form.procedures?.includes(i) ? "border-teal-600 bg-teal-50 text-teal-700" : "border-slate-300"}`}>{i}</button>
                ))}
                {/* INJECTION & FLUIDS — multi-entry pills open the structured editor */}
                {(() => {
                  const injCount = (form.procedure_entries || []).filter(e => e.name === "INJECTION").length;
                  return (
                    <button type="button" data-testid="proc-INJECTION"
                      onClick={() => {
                        const next = [...(form.procedure_entries || []), {
                          id: `inj-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
                          name: "INJECTION", route: "IV", drug: "", note: "",
                        }];
                        setForm((f) => ({
                          ...f, procedure_entries: next,
                          procedures: [...(f.procedures || []), "INJECTION"],
                        }));
                      }}
                      className={`px-3 py-1 text-xs border rounded-sm flex items-center gap-1 ${injCount > 0 ? "border-rose-500 bg-rose-50 text-rose-700 font-semibold" : "border-slate-300"}`}>
                      + INJECTION{injCount > 0 ? ` (${injCount})` : ""}
                    </button>
                  );
                })()}
                {(() => {
                  const fCount = (form.procedure_entries || []).filter(e => e.name === "FLUIDS").length;
                  return (
                    <button type="button" data-testid="proc-FLUIDS"
                      onClick={() => {
                        const next = [...(form.procedure_entries || []), {
                          id: `fld-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
                          name: "FLUIDS", route: "IV", drug: "NS (Normal Saline)",
                          volume: "500 ml", rate: "", note: "",
                        }];
                        setForm((f) => ({
                          ...f, procedure_entries: next,
                          procedures: [...(f.procedures || []), "FLUIDS"],
                        }));
                      }}
                      className={`px-3 py-1 text-xs border rounded-sm flex items-center gap-1 ${fCount > 0 ? "border-blue-500 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300"}`}>
                      + FLUIDS{fCount > 0 ? ` (${fCount})` : ""}
                    </button>
                  );
                })()}
              </div>

              {/* Simple-procedure notes (PLASTER, DRESSING, etc.) */}
              {(form.procedures || []).filter(p => !["INJECTION", "FLUIDS"].includes(p)).length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {(form.procedures || []).filter(p => !["INJECTION", "FLUIDS"].includes(p)).map((p) => (
                    <div key={`proc-note-${p}`} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase w-32 text-teal-700 flex-shrink-0">{p}</span>
                      <Input
                        data-testid={`proc-note-${p}`}
                        value={(form.procedure_notes || {})[p] || ""}
                        onChange={(e) => set("procedure_notes", { ...(form.procedure_notes || {}), [p]: e.target.value })}
                        placeholder="Optional note — site, side, size, duration (prints on Rx)"
                        className="h-7 text-[11px] flex-1"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Structured INJECTION / FLUIDS entries */}
              {(form.procedure_entries || []).length > 0 && (
                <div className="mt-3 border border-slate-200 rounded-sm overflow-hidden" data-testid="proc-entries">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr className="text-left">
                        <th className="label-tiny p-1.5 w-24">Type</th>
                        <th className="label-tiny p-1.5 w-20">Route</th>
                        <th className="label-tiny p-1.5">Drug / Fluid</th>
                        <th className="label-tiny p-1.5 w-32">Dose / Volume</th>
                        <th className="label-tiny p-1.5">Note</th>
                        <th className="label-tiny p-1.5 w-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.procedure_entries || []).map((e, idx) => {
                        const isInj = e.name === "INJECTION";
                        const routes = isInj ? INJECTION_ROUTES : FLUID_ROUTES;
                        const update = (patch) => {
                          const next = [...(form.procedure_entries || [])];
                          next[idx] = { ...next[idx], ...patch };
                          setForm((f) => ({ ...f, procedure_entries: next }));
                        };
                        const remove = () => {
                          const next = (form.procedure_entries || []).filter((_, i) => i !== idx);
                          // Rebuild procedures array from remaining entries + simple procs
                          const simple = (form.procedures || []).filter(p => !["INJECTION", "FLUIDS"].includes(p));
                          setForm((f) => ({
                            ...f,
                            procedure_entries: next,
                            procedures: [...simple, ...next.map(x => x.name)],
                          }));
                        };
                        return (
                          <tr key={e.id || idx} className="border-t">
                            <td className="p-1">
                              <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${isInj ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"}`}>
                                {e.name}
                              </span>
                            </td>
                            <td className="p-1">
                              <select
                                data-testid={`entry-route-${idx}`}
                                value={e.route || routes[0]}
                                onChange={(ev) => update({ route: ev.target.value })}
                                className="h-7 border rounded-sm text-xs px-1 w-full">
                                {routes.map(r => <option key={r} value={r}>{r}</option>)}
                              </select>
                            </td>
                            <td className="p-1">
                              {isInj ? (
                                <Input
                                  data-testid={`entry-drug-${idx}`}
                                  value={e.drug || ""}
                                  onChange={(ev) => update({ drug: ev.target.value })}
                                  placeholder="e.g. Cefotaxime 1g, Pantoprazole 40mg"
                                  className="h-7 text-[11px]"
                                />
                              ) : (
                                <select
                                  data-testid={`entry-drug-${idx}`}
                                  value={e.drug || ""}
                                  onChange={(ev) => update({ drug: ev.target.value })}
                                  className="h-7 border rounded-sm text-xs px-1 w-full">
                                  {FLUID_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              )}
                            </td>
                            <td className="p-1">
                              <Input
                                data-testid={`entry-dose-${idx}`}
                                value={isInj ? (e.dose || "") : (e.volume || "")}
                                onChange={(ev) => update(isInj ? { dose: ev.target.value } : { volume: ev.target.value })}
                                placeholder={isInj ? "Stat / BD × 3d" : "500 ml @ 30 dpm"}
                                className="h-7 text-[11px]"
                              />
                            </td>
                            <td className="p-1">
                              <Input
                                data-testid={`entry-note-${idx}`}
                                value={e.note || ""}
                                onChange={(ev) => update({ note: ev.target.value })}
                                placeholder="Optional note"
                                className="h-7 text-[11px]"
                              />
                            </td>
                            <td className="p-1 text-center">
                              <button data-testid={`entry-del-${idx}`} onClick={remove} type="button"
                                className="p-1 text-rose-600 hover:bg-rose-50 rounded-sm" title="Remove this entry">
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="bg-slate-50/50 border-t px-2 py-1 text-[10px] text-slate-500 flex items-center justify-between">
                    <span>Each row goes to reception's Advised queue for billing. Click <b>+ INJECTION</b> / <b>+ FLUIDS</b> above to add more.</span>
                  </div>
                </div>
              )}
            </div>
            <div><Label>Advice</Label><VoiceInput testid="opd-advice" value={form.advice || ""} onChange={(v) => set("advice", v)} rows={2} placeholder="Rest, review in 3 days…"/><TemplatePicker type="advice" value={form.advice} onChange={(v) => set("advice", v)} testid="tpl-advice"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Follow-up</Label>
                <div className="flex items-stretch gap-1">
                  <Input data-testid="opd-followup" value={form.follow_up || ""} onChange={(e) => set("follow_up", e.target.value)} placeholder="DD/MM/YYYY" className="flex-1"/>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="icon" data-testid="opd-followup-cal-toggle" className="h-9 w-9 border-slate-300" title="Pick from calendar">
                        <CalendarIcon className="w-4 h-4"/>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={(() => {
                          const v = form.follow_up || "";
                          const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                          if (!m) return undefined;
                          const dt = new Date(parseInt(m[3],10), parseInt(m[2],10) - 1, parseInt(m[1],10));
                          return isNaN(dt.getTime()) ? undefined : dt;
                        })()}
                        onSelect={(dt) => {
                          if (!dt) { set("follow_up", ""); return; }
                          const dd = String(dt.getDate()).padStart(2, "0");
                          const mm = String(dt.getMonth() + 1).padStart(2, "0");
                          set("follow_up", `${dd}/${mm}/${dt.getFullYear()}`);
                        }}
                        disabled={(d) => {
                          const today = new Date(); today.setHours(0,0,0,0);
                          return d < today;
                        }}
                        initialFocus
                        data-testid="opd-followup-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {[
                    { k: "1D", days: 1,  label: "1 Day" },
                    { k: "3D", days: 3,  label: "3 Days" },
                    { k: "5D", days: 5,  label: "5 Days" },
                    { k: "1W", days: 7,  label: "1 Week" },
                    { k: "2W", days: 14, label: "2 Weeks" },
                    { k: "1M", days: 30, label: "1 Month" },
                  ].map((q) => (
                    <button
                      key={q.k}
                      type="button"
                      data-testid={`opd-followup-${q.k}`}
                      onClick={() => {
                        const dt = new Date();
                        dt.setDate(dt.getDate() + q.days);
                        const dd = String(dt.getDate()).padStart(2, "0");
                        const mm = String(dt.getMonth() + 1).padStart(2, "0");
                        const yy = dt.getFullYear();
                        set("follow_up", `${dd}/${mm}/${yy}`);
                      }}
                      className="px-2 py-0.5 text-[11px] border border-slate-300 rounded-sm hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700"
                    >
                      {q.label}
                    </button>
                  ))}
                  {form.follow_up && (
                    <button
                      type="button"
                      data-testid="opd-followup-clear"
                      onClick={() => set("follow_up", "")}
                      className="px-2 py-0.5 text-[11px] border border-red-300 rounded-sm text-red-600 hover:bg-red-50"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <label className="self-end flex items-center gap-2 pb-2"><input type="checkbox" checked={!!form.admission_advice} onChange={(e) => set("admission_advice", e.target.checked)} data-testid="opd-admit"/> Admission Advice</label>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-tiny">Medicines / Prescription</div>
                <div className="text-[11px] text-slate-500">Type to autocomplete from master · click quick-picks · toggle Custom for off-catalog</div>
              </div>
              <Button size="sm" variant="outline" onClick={addMed} data-testid="opd-add-med">+ Add Medicine</Button>
            </div>
            <div className="space-y-3">
              {(form.medicines || []).map((m, i) => (
                <MedicineRow key={m._uid || `med-${i}`} index={i} value={m}
                  custom={!!m._custom}
                  onChange={(nv) => updMed(i, nv)}
                  onRemove={() => rmMed(i)}
                  onToggleCustom={() => toggleCustom(i)}
                />
              ))}
              {(form.medicines || []).length === 0 && (
                <div className="p-4 text-center text-xs text-slate-500 border border-dashed">No medicines added. Click <b>+ Add Medicine</b>.</div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Quick Print Bundle — tick and go */}
          <PrintBundle opdId={id} />

          <AIAssistCard opdId={id} form={form} set={set} />

          <div className="bg-white border border-slate-200 p-4">
            <div className="label-tiny mb-3">Vitals</div>
            <div className="grid grid-cols-2 gap-3">
              {[["bp","BP mmHg"],["pr","PR /min"],["rs","RS /min"],["temp","Temp °F"],["spo2","SpO2 %"],["wt","Weight kg"],["ht","Height cm"]].map(([k,l])=>(
                <div key={k}><Label className="text-xs">{l}</Label><Input value={v[k]||""} onChange={(e)=>setV(k,e.target.value)} className="h-9" data-testid={`opd-vital-${k}`}/></div>
              ))}
            </div>
          </div>

          {/* Billing Summary Panel */}
          <div className="bg-white border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-tiny">OPD Billing Summary</div>
              {(can("billing", "create") || can("billing", "request_discount")) && !bill?.pending_discount && (
                <Button size="sm" variant="outline" onClick={() => setDiscOpen(true)} data-testid="request-discount-btn" className="rounded-sm h-8"><Percent className="w-3 h-3 mr-1"/> Request Discount</Button>
              )}
            </div>
            {bill ? (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Consultation</span><span className="font-mono">{fmtINR(bill.consultation)}</span></div>
                {bill.investigations > 0 && <div className="flex justify-between"><span className="text-slate-500">Investigations</span><span className="font-mono">{fmtINR(bill.investigations)}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">Procedures</span><span className="font-mono">{fmtINR(bill.procedures)}</span></div>
                {bill.other > 0 && <div className="flex justify-between"><span className="text-slate-500">Other</span><span className="font-mono">{fmtINR(bill.other)}</span></div>}
                <div className="border-t pt-1 flex justify-between font-semibold"><span>Gross</span><span className="font-mono">{fmtINR(bill.gross)}</span></div>
                {bill.discount_approved > 0 && <div className="flex justify-between text-teal-700"><span>Discount (Approved)</span><span className="font-mono">- {fmtINR(bill.discount_approved)}</span></div>}
                {bill.pending_discount && (
                  <div className="flex justify-between text-amber-700">
                    <span>Discount (Pending)</span>
                    <span className="font-mono">₹{bill.pending_discount.amount} awaiting approval</span>
                  </div>
                )}
                <div className="border-t pt-1 flex justify-between text-lg font-bold text-blue-800"><span>Net Payable</span><span className="font-mono">{fmtINR(bill.net)}</span></div>
                <div className="flex justify-between text-xs text-slate-500"><span>Paid</span><span className="font-mono">{fmtINR(bill.paid)}</span></div>
                <div className="flex justify-between text-sm"><span className="font-semibold">Balance</span><span className={`font-mono font-semibold ${bill.balance > 0 ? "text-red-700" : "text-teal-700"}`}>{fmtINR(bill.balance)}</span></div>
              </div>
            ) : <div className="text-xs text-slate-500">Loading...</div>}
          </div>
        </div>
      </div>

      {/* Investigation detail dialog */}
      <InvestigationDetailDialog
        open={!!invDetailFor}
        master={invDetailFor}
        existing={invDetailFor ? (form.investigation_notes || {})[invDetailFor.name] : null}
        onClose={() => setInvDetailFor(null)}
        onSave={(payload) => {
          const name = invDetailFor.name;
          const nextNotes = { ...(form.investigation_notes || {}), [name]: payload };
          const arr = form.investigations || [];
          const nextArr = arr.includes(name) ? arr : [...arr, name];
          setForm((f) => ({ ...f, investigations: nextArr, investigation_notes: nextNotes }));
          setInvDetailFor(null);
        }}
      />

      {/* Full Rx preview dialog — renders PrescriptionPrint (?preview=1 so it doesn't auto-print) inside an iframe */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4"/> Rx Preview · {opd.opd_no} · {opd.patient_name}
            </DialogTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setPreviewKey((k) => k + 1)} data-testid="opd-preview-refresh">Refresh</Button>
              <Button size="sm" onClick={() => window.open(`/prescription/${id}/print`, "_blank")} className="bg-blue-700 hover:bg-blue-800" data-testid="opd-preview-print">
                <Printer className="w-4 h-4 mr-1"/> Print now
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 bg-slate-100 overflow-hidden">
            <iframe
              key={previewKey}
              src={`/prescription/${id}/print?preview=1`}
              title="Rx Preview"
              data-testid="opd-preview-iframe"
              className="w-full h-full border-0"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Discount request dialog */}
      <Dialog open={discOpen} onOpenChange={setDiscOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Discount</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600 bg-amber-50 border border-amber-200 p-2 rounded-sm">This request will be sent to an Admin for approval. The discount is applied only after approval.</p>
            <div>
              <Label>Discount Amount (₹)</Label>
              <Input type="number" data-testid="disc-amount" value={disc.amount || ""} onChange={(e) => setDisc({ ...disc, amount: e.target.value })} />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea data-testid="disc-reason" value={disc.reason} onChange={(e) => setDisc({ ...disc, reason: e.target.value })} rows={2} placeholder="Repeat patient / staff relative / concession..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscOpen(false)}>Cancel</Button>
            <Button onClick={requestDiscount} data-testid="disc-submit" className="bg-blue-700 hover:bg-blue-800">Submit for Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Doctor dialog */}
      <Dialog open={docDlgOpen} onOpenChange={setDocDlgOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Doctor · {opd.opd_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2">
              Current: <b>{opd.doctor_name || "Not assigned"}</b> · {opd.department || "—"}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">New Doctor</Label>
              <select
                data-testid="opd-change-doctor-select"
                className="h-9 mt-0.5 w-full border rounded-sm px-2 text-sm bg-white"
                value={docChange.doctor_id}
                onChange={(e) => setDocChange({ ...docChange, doctor_id: e.target.value })}
              >
                <option value="">-- Select --</option>
                {docList.filter((d) => d.id !== opd.doctor_id).map((d) => (
                  <option key={d.id} value={d.id}>{d.name} · {d.department || "—"} · ₹{d.consultation_fee || 0}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Reason (required, audit-logged)</Label>
              <Textarea
                data-testid="opd-change-doctor-reason"
                rows={2}
                className="text-sm"
                placeholder="e.g. Wrong doctor selected at OPD registration"
                value={docChange.reason}
                onChange={(e) => setDocChange({ ...docChange, reason: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                data-testid="opd-change-doctor-adjust-fee"
                checked={docChange.adjust_fee}
                onChange={(e) => setDocChange({ ...docChange, adjust_fee: e.target.checked })}
              />
              Update consultation fee to new doctor's default rate
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocDlgOpen(false)}>Cancel</Button>
            <Button
              onClick={submitDoctorChange}
              data-testid="opd-change-doctor-confirm"
              className="bg-blue-700 hover:bg-blue-800 rounded-sm"
              disabled={!docChange.doctor_id || !docChange.reason.trim()}
            >
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
